import { useEffect, useMemo, useState } from "react";
import {
  UserPlus,
  RefreshCw,
  X,
  Trash2,
  Copy,
  Check,
  MoreHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  ApiError,
  type EmailDeliveryStatus,
  type Invitation,
  type Member,
  type Role,
  createInvitation,
  getInvitations,
  getMembers,
  removeMember,
  resendInvitation,
  revokeInvitation,
  updateMemberRole,
} from "@/services/api";

// Map structured server error codes to user-visible copy. Anything we don't
// recognise falls through to the server's own message / status code.
const errorMessage = (err: unknown, fallback: string): string => {
  if (err instanceof ApiError && err.code) {
    const map: Record<string, string> = {
      INVALID_EMAIL: "That email address doesn't look valid.",
      INVALID_ROLE: "Please choose a role.",
      ALREADY_MEMBER_THIS_COMPANY: "That person is already a member of this company.",
      ALREADY_MEMBER_OTHER_COMPANY: "That email is already linked to another company.",
      LAST_ADMIN_CANNOT_BE_DEMOTED: "You can't demote the last admin. Promote someone else first.",
      LAST_ADMIN_CANNOT_BE_REMOVED: "You can't remove the last admin. Promote someone else first.",
      CANNOT_REMOVE_SELF: "You can't remove yourself.",
      INVITATION_NOT_PENDING: "That invitation is no longer pending.",
    };
    if (map[err.code]) return map[err.code];
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
};

// Status → badge style. Keeps colours consistent with shadcn conventions.
const statusBadgeVariant = (status: Invitation["status"]): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case "PENDING":
      return "default";
    case "ACCEPTED":
      return "secondary";
    case "REVOKED":
    case "EXPIRED":
      return "outline";
  }
};

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

const relativeExpiry = (iso: string): string => {
  const diffMs = new Date(iso).getTime() - Date.now();
  if (diffMs <= 0) return "expired";
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days >= 1) return `in ${days} day${days === 1 ? "" : "s"}`;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours >= 1) return `in ${hours} hour${hours === 1 ? "" : "s"}`;
  const minutes = Math.max(1, Math.floor(diffMs / (1000 * 60)));
  return `in ${minutes} minute${minutes === 1 ? "" : "s"}`;
};

const TeamPage = () => {
  const { appUser } = useAuth();
  const { toast } = useToast();

  const isAdmin = appUser?.role === "ADMIN";
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<Role>("MEMBER");
  const [inviting, setInviting] = useState(false);

  // After create/resend we always show a confirmation dialog so the admin
  // sees what happened. When email delivery actually succeeded, the dialog
  // is a simple "sent!" message. When the backend is in log-only mode, is
  // missing an API key, or the provider rejected the message, we surface
  // the accept URL with a copy button as a fallback delivery channel.
  const [lastAcceptUrl, setLastAcceptUrl] = useState<string | null>(null);
  const [lastAcceptEmail, setLastAcceptEmail] = useState<string | null>(null);
  const [lastDelivery, setLastDelivery] = useState<EmailDeliveryStatus | null>(null);
  const [urlCopied, setUrlCopied] = useState(false);

  const [confirmRemoveMember, setConfirmRemoveMember] = useState<Member | null>(null);
  const [confirmRevokeInvite, setConfirmRevokeInvite] = useState<Invitation | null>(null);

  const pendingInvites = useMemo(
    () => invitations.filter((i) => i.status === "PENDING"),
    [invitations],
  );

  const load = async () => {
    try {
      setLoading(true);
      if (isAdmin) {
        const [m, inv] = await Promise.all([getMembers(), getInvitations()]);
        setMembers(m);
        setInvitations(inv);
      } else {
        // Non-admins can see the member list but not the invite list.
        const m = await getMembers();
        setMembers(m);
      }
    } catch (err) {
      toast({
        title: "Error",
        description: errorMessage(err, "Failed to load the team"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Count of admins excluding the selected target, used to disable actions
  // that would leave the company with zero admins.
  const adminCount = useMemo(() => members.filter((m) => m.role === "ADMIN").length, [members]);

  const onSubmitInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const result = await createInvitation(inviteEmail.trim().toLowerCase(), inviteRole);
      setInviteDialogOpen(false);
      setInviteEmail("");
      setInviteRole("MEMBER");
      setLastAcceptUrl(result.acceptUrl);
      setLastAcceptEmail(result.invitation.email);
      setLastDelivery(result.emailDelivery);
      setUrlCopied(false);
      toast({
        title: result.emailDelivery.delivered ? "Invite sent" : "Invite created",
        description: result.emailDelivery.delivered
          ? `An email was sent to ${result.invitation.email}.`
          : `Share the link with ${result.invitation.email}.`,
      });
      await load();
    } catch (err) {
      toast({
        title: "Couldn't create invite",
        description: errorMessage(err, "Failed to create invitation"),
        variant: "destructive",
      });
    } finally {
      setInviting(false);
    }
  };

  const onResend = async (invitation: Invitation) => {
    try {
      const result = await resendInvitation(invitation.id);
      setLastAcceptUrl(result.acceptUrl);
      setLastAcceptEmail(result.invitation.email);
      setLastDelivery(result.emailDelivery);
      setUrlCopied(false);
      toast({
        title: "Invite resent",
        description: result.emailDelivery.delivered
          ? `A new email was sent to ${result.invitation.email}.`
          : `Share the fresh link with ${result.invitation.email}.`,
      });
      await load();
    } catch (err) {
      toast({
        title: "Couldn't resend invite",
        description: errorMessage(err, "Failed to resend invitation"),
        variant: "destructive",
      });
    }
  };

  const onRevoke = async () => {
    if (!confirmRevokeInvite) return;
    try {
      await revokeInvitation(confirmRevokeInvite.id);
      toast({ title: "Invite revoked" });
      await load();
    } catch (err) {
      toast({
        title: "Couldn't revoke invite",
        description: errorMessage(err, "Failed to revoke invitation"),
        variant: "destructive",
      });
    } finally {
      setConfirmRevokeInvite(null);
    }
  };

  const onChangeRole = async (member: Member, role: Role) => {
    if (member.role === role) return;
    try {
      await updateMemberRole(member.id, role);
      toast({ title: "Role updated", description: `${member.fullName} is now ${role}.` });
      await load();
    } catch (err) {
      toast({
        title: "Couldn't update role",
        description: errorMessage(err, "Failed to update role"),
        variant: "destructive",
      });
    }
  };

  const onRemoveMember = async () => {
    if (!confirmRemoveMember) return;
    try {
      await removeMember(confirmRemoveMember.id);
      toast({ title: "Member removed", description: `${confirmRemoveMember.fullName} has been removed.` });
      await load();
    } catch (err) {
      toast({
        title: "Couldn't remove member",
        description: errorMessage(err, "Failed to remove member"),
        variant: "destructive",
      });
    } finally {
      setConfirmRemoveMember(null);
    }
  };

  const copyAcceptUrl = async () => {
    if (!lastAcceptUrl) return;
    try {
      await navigator.clipboard.writeText(lastAcceptUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch {
      toast({
        title: "Couldn't copy",
        description: "Please copy the URL manually.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Manage who has access to your company workspace.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setInviteDialogOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Invite member
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>Everyone with access to this company.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                {isAdmin && <TableHead className="w-[60px]" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => {
                const isSelf = appUser?.id === m.id;
                const isOnlyAdmin = m.role === "ADMIN" && adminCount === 1;
                return (
                  <TableRow key={m.id}>
                    <TableCell className="font-medium">{m.fullName}</TableCell>
                    <TableCell>{m.email ?? "—"}</TableCell>
                    <TableCell>
                      <Badge variant={m.role === "ADMIN" ? "default" : "secondary"}>
                        {m.role}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(m.createdAt)}</TableCell>
                    {isAdmin && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label="Open actions">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {m.role !== "ADMIN" && (
                              <DropdownMenuItem onClick={() => onChangeRole(m, "ADMIN")}>
                                Promote to admin
                              </DropdownMenuItem>
                            )}
                            {m.role === "ADMIN" && !isOnlyAdmin && (
                              <DropdownMenuItem onClick={() => onChangeRole(m, "MEMBER")}>
                                Demote to member
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              disabled={isSelf || isOnlyAdmin}
                              onClick={() => setConfirmRemoveMember(m)}
                              className="text-destructive focus:text-destructive"
                            >
                              Remove member
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle>Pending invites</CardTitle>
            <CardDescription>
              People who have been invited but haven't joined yet.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pendingInvites.length === 0 ? (
              <p className="text-sm text-muted-foreground">No pending invites.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Invited by</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead className="w-[120px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingInvites.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell className="font-medium">{inv.email}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{inv.role}</Badge>
                      </TableCell>
                      <TableCell>{inv.invitedBy?.fullName ?? "—"}</TableCell>
                      <TableCell>
                        <Badge variant={statusBadgeVariant(inv.status)}>
                          {relativeExpiry(inv.expiresAt)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Resend invite"
                            onClick={() => onResend(inv)}
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Revoke invite"
                            onClick={() => setConfirmRevokeInvite(inv)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Invite member dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent>
          <form onSubmit={onSubmitInvite}>
            <DialogHeader>
              <DialogTitle>Invite a team member</DialogTitle>
              <DialogDescription>
                They'll get a link to create their account and join your workspace.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="invite-email">Email</Label>
                <Input
                  id="invite-email"
                  type="email"
                  required
                  autoFocus
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="teammate@example.com"
                  disabled={inviting}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="invite-role">Role</Label>
                <Select
                  value={inviteRole}
                  onValueChange={(value) => setInviteRole(value as Role)}
                  disabled={inviting}
                >
                  <SelectTrigger id="invite-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MEMBER">Member</SelectItem>
                    <SelectItem value="ADMIN">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setInviteDialogOpen(false)}
                disabled={inviting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={inviting}>
                {inviting ? "Sending..." : "Send invite"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Post-invite confirmation dialog. Adapts to whether the mailer
          delivered successfully. In the delivered case we still expose the
          accept URL as a secondary channel (e.g. invitee says they didn't
          get the email, spam filter, etc.) behind a disclosure. */}
      <Dialog
        open={lastAcceptUrl !== null}
        onOpenChange={(open) => { if (!open) setLastAcceptUrl(null); }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {lastDelivery?.delivered
                ? "Invitation email sent"
                : "Share this invite link"}
            </DialogTitle>
            <DialogDescription>
              {lastDelivery?.delivered ? (
                <>
                  We emailed the invite to{" "}
                  <span className="font-medium">{lastAcceptEmail}</span>. The link expires in 7 days.
                </>
              ) : lastDelivery?.logOnly ? (
                <>
                  Email delivery is off in this environment, so we couldn't email{" "}
                  <span className="font-medium">{lastAcceptEmail}</span> directly. Share this link with them instead — it expires in 7 days.
                </>
              ) : (
                <>
                  We couldn't email{" "}
                  <span className="font-medium">{lastAcceptEmail}</span> this time. You can share the link directly — it expires in 7 days.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input readOnly value={lastAcceptUrl ?? ""} onFocus={(e) => e.target.select()} />
            <Button variant="outline" onClick={copyAcceptUrl}>
              {urlCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setLastAcceptUrl(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm remove member */}
      <AlertDialog
        open={confirmRemoveMember !== null}
        onOpenChange={(open) => { if (!open) setConfirmRemoveMember(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this member?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmRemoveMember?.fullName} will lose access to the workspace immediately. Their account will be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onRemoveMember} className="bg-destructive hover:bg-destructive/90">
              <Trash2 className="h-4 w-4 mr-2" />
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm revoke invite */}
      <AlertDialog
        open={confirmRevokeInvite !== null}
        onOpenChange={(open) => { if (!open) setConfirmRevokeInvite(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this invite?</AlertDialogTitle>
            <AlertDialogDescription>
              The invite link for {confirmRevokeInvite?.email} will stop working. You can always send a new one later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onRevoke}>Revoke</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TeamPage;
